import { useDispatch, useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { green } from '@mui/material/colors';
import { VEHICLE_TYPE, FUEL_TYPE } from './contants';
import {
  selectVehicleInfo,
  selectLocationInfo,
  selectTransactionInfo,
} from '../../redux-state/selectors';
import { DECODE_VIN } from '../../redux-state/fees/types';
import { useEffect, useRef, useState } from 'react';
import CurrencyTextField from '../common/CurrencyField'
import { getRequiredVehicleSpecFields } from '../utility/stateRequirements';
import {
  getRegistrationMonthsOptions,
  getRegistrationMonthsSelection,
  reconcileRegistrationMonths,
  isRegistrationMonthsRequired,
} from '../utility/registrationMonthsField';


function VehicleInfoForm({ errors }) {
  const vehicleInfo = useSelector(selectVehicleInfo);
  const locationInfo = useSelector(selectLocationInfo);
  const transactionInfo = useSelector(selectTransactionInfo);
  const dispatch = useDispatch();

  // Per-state GVW / Weight / MPG requirements, using the same tables the
  // bot checks (utility/stateRequirements.js, ported from
  // state_requirements.py). A field only picks up the red asterisk once a
  // state that requires it is selected — and, where the ATC guide scopes
  // the requirement to particular vehicle types, only for those types.
  // With no state selected nothing here is required.
  const requiredSpecs = getRequiredVehicleSpecFields(
    'fees',
    locationInfo?.state,
    vehicleInfo?.vehicleType
  );
  const isGvwRequired = requiredSpecs.gvw;
  // GVWR is its own ATC tag, and the states that ask for it (CT, KS, LA, NJ,
  // TN, VA) reject the quote outright when it is absent -- "Required data
  // missing in path, for Registration Fee //ServiceRequest/Vehicle/GVWR" --
  // no matter what Vehicle Weight and GVW hold.
  const isGvwrRequired = requiredSpecs.gvwr;
  // <MPG> is a single value in the ATC spec and this form sends
  // `MPG: data.cityMpg || data.mpg`, so City MPG is what satisfies it.
  // Highway and Combined MPG are not required by any state.
  const isCityMpgRequired = requiredSpecs.mpg;
  const isHighwayMpgRequired = false;
  const isCombinedMpgRequired = false;

  // Registration term. A dropdown rather than a free-text box, because the
  // term is not a number the user invents — every state issues a fixed,
  // knowable set, and typing 18 into a state that sells 12 or 24 produced a
  // quote nobody could actually buy. Before a state is known the list is the
  // documented API default (a single 12) shown but unselected; once the ZIP
  // decode resolves a state the list becomes exactly the terms that state
  // issues, with its default — always the lowest it supports — selected.
  // See utility/registrationMonths.js for the tables and their sources.
  // vehicleType matters as much as the state here: most states write their longer
  // term for a "passenger motor vehicle" and leave motorcycles, trailers and
  // commercial trucks on an ordinary annual cycle, so a Massachusetts motorcycle is
  // 12 months where a Massachusetts car is 24.
  const registrationMonthsContext = {
    state: locationInfo?.state,
    titleStatus: transactionInfo?.titleStatus,
    modelYear: vehicleInfo?.modelYear,
    vehicleType: vehicleInfo?.vehicleType,
    registrationMonths: vehicleInfo?.registrationMonths,
  };
  const registrationMonthsOptions = getRegistrationMonthsOptions(registrationMonthsContext);
  const isRegistrationMonthsFieldRequired = isRegistrationMonthsRequired(locationInfo?.state);

  // Moving between states always resets the term to the new state's default, even
  // when the new state issues the term the user had picked — see
  // reconcileRegistrationMonths. That needs history the rest of this component does
  // not keep, so the state each reconcile ran against is remembered in a ref: it is
  // written in the effect below rather than during render, and it is deliberately
  // NOT a piece of redux state, because it describes this field's own bookkeeping
  // and nothing else reads it.
  const previousRegistrationStateRef = useRef(undefined);
  const reconciledRegistrationMonths = reconcileRegistrationMonths(
    registrationMonthsContext,
    previousRegistrationStateRef.current
  );

  // Showing the reconciled value straight away avoids a frame where the dropdown
  // still displays the old state's term. Without it, moving from a 24-month pick in
  // PA to OH would paint 24 once — OH issues 24, so nothing filters it — and only
  // flip to 12 after the effect landed.
  const registrationMonthsValue = reconciledRegistrationMonths !== null
    ? reconciledRegistrationMonths
    : getRegistrationMonthsSelection(registrationMonthsContext);

  useEffect(() => {
    // The state goes in the dependency list as well as the ref: a state change that
    // happens to land on the same term (PA -> MD, both 12) reconciles to null, and
    // without the state here the effect would not re-run to move the ref forward.
    previousRegistrationStateRef.current = locationInfo?.state;
    if (reconciledRegistrationMonths === null) return;
    dispatch({
      type: 'UPDATE_VEHICLE_INFO',
      payload: { key: 'registrationMonths', value: reconciledRegistrationMonths },
    });
  }, [reconciledRegistrationMonths, locationInfo?.state, dispatch]);

  const [localError, setLocalError] = useState('');

  const validateVIN = (vin) => /^[A-HJ-NPR-Z0-9]{17}$/.test(vin.trim());

  const decodeVinCode = () => {
    if (!validateVIN(vehicleInfo.vinCode)) {
      setLocalError('Please enter a valid 17-character VIN');
      return;
    }

    setLocalError('');
    dispatch({
      type: DECODE_VIN,
      payload: { vin: vehicleInfo.vinCode } });
  };

  return (
    <div className="flex flex-col w-full">
      <div className="p-4 rounded-lg bg-white text-gray-700">
        <div className="text-lg font-bold">Vehicle Information</div>
        <div className="flex flex-wrap justify-between mt-2 text-sm">
          <div className="text-gray-600">
            Add correct vehicle details
          </div>
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <TextField
            required
            label="VIN Number"
            variant="outlined"
            fullWidth
            value={vehicleInfo.vinCode}
            onChange={(e) => {
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'vinCode', value: e.target.value },
              });
            }}
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.vinCode || !!localError}
            helperText={errors?.vinCode || localError}
          />

          <Button
            onClick={decodeVinCode}
            variant="outlined"
            sx={{
              minWidth: '150px',
              height: '55px',
              color: '#229954',
              borderRadius: '10px',
              borderColor: '#229954',
              '&:hover': {
                backgroundColor: green[900],
                color: '#fff',
              },
            }}
          >
            Decode VIN
          </Button>
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <FormControl fullWidth required error={errors?.vehicleType}>
            <InputLabel>Vehicle Type</InputLabel>
            <Select
              label="Vehicle Type"
              value={vehicleInfo.vehicleType}
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_VEHICLE_INFO',
                  payload: { key: 'vehicleType', value: e.target.value },
                })
              }
            >
              {Object.keys(VEHICLE_TYPE).map((object, index) => (
                <MenuItem
                  key={index}
                  value={object}
                  className={object === "Multipurpose Passenger Vehicle" ? "hidden-important" : ""}
                >
                  {VEHICLE_TYPE[object]}
                </MenuItem>
              ))}
            </Select>
            {errors?.vehicleType && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>{errors.vehicleType}</div>
            )}
          </FormControl>

          <FormControl fullWidth required error={errors?.fuelType}>
            <InputLabel>Fuel Type</InputLabel>
            <Select
              label="Fuel Type"
              value={vehicleInfo.fuelType}
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_VEHICLE_INFO',
                  payload: { key: 'fuelType', value: e.target.value },
                })
              }
            >
              {Object.keys(FUEL_TYPE).map((key, index) => (
                <MenuItem key={index} value={key}>
                  {FUEL_TYPE[key]}
                </MenuItem>
              ))}
            </Select>
            {errors?.fuelType && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>{errors.fuelType}</div>
            )}
          </FormControl>
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <TextField
            required
            type="number"
            label="Vehicle Weight"
            variant="outlined"
            fullWidth
            value={vehicleInfo.vehicleWeight}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'vehicleWeight', value: e.target.value },
              })
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Select
                    value="lbs"
                    variant="standard"
                    disableUnderline
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="lbs">lbs</MenuItem>
                  </Select>
                </InputAdornment>
              ),
              style: { borderRadius: '10px' },
            }}
            error={!!errors?.vehicleWeight}
            helperText={errors?.vehicleWeight}
          />

          <TextField
            label="GVW"
            variant="outlined"
            fullWidth
            required={isGvwRequired}
            value={vehicleInfo.gvw}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'gvw', value: e.target.value },
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.gvw}
            helperText={errors?.gvw}
          />

          {/* GVWR -- the manufacturer's gross vehicle weight *rating*, sent as
              its own <GVWR> tag. Autofilled from the VIN decode's gross weight
              where VINAudit publishes one, but editable: it is printed on the
              door jamb sticker, so the user can always read the real figure
              off the vehicle. Required only for the states in
              GVWR_REQUIREMENTS, which reject the quote without it. */}
          <TextField
            label="GVWR"
            type="number"
            variant="outlined"
            fullWidth
            required={isGvwrRequired}
            value={vehicleInfo.gvwr ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'gvwr', value: e.target.value },
              })
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <span style={{ fontSize: '0.875rem' }}>lbs</span>
                </InputAdornment>
              ),
              style: { borderRadius: '10px' },
            }}
            error={!!errors?.gvwr}
            helperText={errors?.gvwr}
          />
        </div>

        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <CurrencyTextField
            label="MSRP"
            name="msrp"
            required
            value={vehicleInfo.msrp ?? ''} // keep coSntrolled
            onChange={(num) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'msrp', value: num }, // <-- num is a Number or '' when cleared
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.msrp}
            helperText={errors?.msrp}
          />

          <TextField
            required
            label="Model Year"
            type="number"
            variant="outlined"
            fullWidth
            value={vehicleInfo.modelYear}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'modelYear', value: e.target.value },
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.modelYear}
            helperText={errors?.modelYear}
          />
        </div>

        {/* Make / Model. Autofilled by the VIN decode response, but remain
            editable. These feed the Make/Model rows on the fee summary PDF. */}
        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <TextField
            label="Make"
            variant="outlined"
            fullWidth
            value={vehicleInfo.make ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'make', value: e.target.value },
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.make}
            helperText={errors?.make}
          />

          <TextField
            label="Model"
            variant="outlined"
            fullWidth
            value={vehicleInfo.model ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'model', value: e.target.value },
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.model}
            helperText={errors?.model}
          />
        </div>

        {/* Registration term sent as <RegistrationMonths>. The options are the
            terms the selected state actually issues, so a customer electing a
            term the state merely offers (PA, VA, WV, VT, FL, OK, MO, OH, MI,
            AZ, NM, ID, MT, GA, AL, MD, DE) can still be quoted for it, while a
            state that issues only one term shows only that one. */}
        <div className="flex flex-col md:flex-row mt-4 gap-4">
          <FormControl
            fullWidth
            required={isRegistrationMonthsFieldRequired}
            error={!!errors?.registrationMonths}
          >
            <InputLabel>Registration Months</InputLabel>
            <Select
              label="Registration Months"
              value={registrationMonthsValue}
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_VEHICLE_INFO',
                  payload: { key: 'registrationMonths', value: e.target.value },
                })
              }
              sx={{ borderRadius: '10px' }}
            >
              {registrationMonthsOptions.map((months) => (
                <MenuItem key={months} value={months}>
                  {months}
                </MenuItem>
              ))}
            </Select>
            {errors?.registrationMonths && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>
                {errors.registrationMonths}
              </div>
            )}
          </FormControl>

          {/* Empty half of the row. Without it the single `fullWidth` input
              stretches across the whole row and reads as a different field
              size from every paired row above; this keeps it to one of two
              columns. Hidden below `md`, where the row stacks anyway. */}
          <div className="hidden md:block w-full" aria-hidden="true" />
        </div>

        {/* Broken-out MPG fields per ATC AGF Consumption Guide:
            <MPG> = City miles per gallon (Integer)
            <HighwayMPG> = Highway miles per gallon (Integer)
            <CombinedMPG> = Combined miles per gallon (Integer)
            Autofilled by the VIN decode response, but remain editable. */}
        <div className="flex flex-col md:flex-row mt-4 gap-4 pb-4">
          <TextField
            label="MPG (City)"
            type="number"
            variant="outlined"
            fullWidth
            required={isCityMpgRequired}
            value={vehicleInfo.cityMpg ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'cityMpg', value: e.target.value },
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.cityMpg}
            helperText={errors?.cityMpg}
          />

          <TextField
            label="Highway MPG"
            type="number"
            variant="outlined"
            fullWidth
            required={isHighwayMpgRequired}
            value={vehicleInfo.highwayMpg ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'highwayMpg', value: e.target.value },
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.highwayMpg}
            helperText={errors?.highwayMpg}
          />

          <TextField
            label="Combined MPG"
            type="number"
            variant="outlined"
            fullWidth
            required={isCombinedMpgRequired}
            value={vehicleInfo.combinedMpg ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_VEHICLE_INFO',
                payload: { key: 'combinedMpg', value: e.target.value },
              })
            }
            InputProps={{ style: { borderRadius: '10px' } }}
            error={!!errors?.combinedMpg}
            helperText={errors?.combinedMpg}
          />
        </div>
      </div>
    </div>
  );
}

export default VehicleInfoForm;
